// ---------------------------------------------------------------------------
// YouAreYou (uru) — Verified Human Demo Page
//
// Route: /quick-check/demo (public)
//
// A static mock of the Verified Human profile page, shown when a user in
// Simple Mode clicks the example activity item. Illustrates what the real
// /p/:code page looks like after someone completes verification.
//
// All data is fake. A banner at the top makes this clear.
// ---------------------------------------------------------------------------

import { Link } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import logoBrand from "@/assets/logo-brand-transparent2.jpg";

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const MOCK = {
  name: "Jamie Turner",
  initials: "JT",
  badges: [
    { icon: "badge",      label: "Government ID" },
    { icon: "face",       label: "Liveness Scan" },
    { icon: "smartphone", label: "Phone Verified" },
  ],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function VerifiedHumanDemoPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-blue-50">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/">
            <img src={logoBrand} alt="YouAreYou" className="h-8 w-auto" />
          </Link>
          <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-700 ring-1 ring-green-200">
            Verified Profile
          </span>
        </div>
      </header>

      {/* Demo banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center">
        <p className="text-xs font-semibold text-amber-700">
          <Icon name="info" className="mr-1 text-sm align-middle" />
          This is an example — showing what a real verified profile looks like.{" "}
          <Link to="/quick-check" className="underline hover:text-amber-900">
            Back to your check page
          </Link>
        </p>
      </div>

      <main className="mx-auto max-w-2xl px-4 pb-16 pt-10 sm:px-6">

        {/* Hero title */}
        <div className="mb-8 text-center">
          <div className="mb-3 flex items-center justify-center gap-2">
            <Icon name="verified" className="text-3xl text-primary-500" />
            <h1 className="text-4xl font-extrabold tracking-tight text-text-primary sm:text-5xl">
              Verified Human
            </h1>
          </div>
          <p className="text-base text-text-secondary">
            Identity independently verified by{" "}
            <span className="font-semibold text-primary-500">YouAreYou</span>
          </p>
        </div>

        {/* Person card */}
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl shadow-blue-100/40">

          {/* Blue header strip */}
          <div className="bg-primary-500 px-6 pb-20 pt-10" />

          {/* Avatar */}
          <div className="-mt-14 flex justify-center px-6">
            <div className="relative">
              <div className="flex h-28 w-28 items-center justify-center rounded-full border-4 border-white bg-primary-100 shadow-md">
                <span className="text-4xl font-extrabold text-primary-400">{MOCK.initials}</span>
              </div>
              <span className="absolute bottom-0.5 right-0.5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-green-500 shadow">
                <Icon name="check" className="text-base text-white" />
              </span>
            </div>
          </div>

          {/* Name + subtitle */}
          <div className="mt-4 px-6 text-center">
            <h2 className="text-2xl font-extrabold text-text-primary">{MOCK.name}</h2>
            <p className="mt-1 text-sm font-medium text-text-secondary">
              Verified Human by{" "}
              <span className="font-semibold text-primary-500">YouAreYou</span>
            </p>
          </div>

          {/* Badges */}
          <div className="mt-6 px-6">
            <p className="mb-3 text-[11px] font-extrabold uppercase tracking-widest text-text-muted">
              Verified Factors
            </p>
            <div className="flex flex-wrap gap-2">
              {MOCK.badges.map((b) => (
                <span
                  key={b.label}
                  className="flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 ring-1 ring-green-200"
                >
                  <Icon name={b.icon} className="text-sm" />
                  {b.label}
                </span>
              ))}
            </div>
          </div>

          <div className="mx-6 my-6 border-t border-gray-100" />

          {/* Explainer */}
          <div className="px-6 pb-6">
            <div className="rounded-2xl bg-blue-50 p-4 ring-1 ring-blue-100">
              <div className="mb-2 flex items-center gap-2">
                <Icon name="info" className="text-base text-primary-500" />
                <p className="text-sm font-bold text-primary-700">What is a Verified Human?</p>
              </div>
              <p className="text-xs leading-relaxed text-text-secondary">
                A Verified Human on YouAreYou has completed independent identity
                checks — government-issued ID, liveness scan, and phone
                verification. This profile gives you confidence that you&apos;re
                dealing with a real, verified person — not a bot, an
                impersonator, or a fake account.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-6">
          <Link
            to="/register"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-4 text-sm font-bold text-white shadow-sm transition-colors hover:bg-primary-600"
          >
            <Icon name="verified_user" className="text-lg" />
            Verify you are human
          </Link>
          <p className="mt-2.5 text-center text-xs text-text-muted">
            Get your own verified identity profile. Free to start.
          </p>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-text-muted">
            Powered by <span className="font-semibold text-text-secondary">MojoVerify</span>
          </p>
        </div>
      </main>
    </div>
  );
}
